import Box from '@mui/material/Box';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type { EntitySummary, EventSummary, NarrativeSummary, StructureSection } from '@/lib/types';

import AuthGuard from '@/components/auth-guard';
import { FeatureGuard } from '@/components/feature-guard';
import { NarrativeFilters } from '@/components/narrative/narrative-filters';
import { NarrativeGraphView } from '@/components/narrative/narrative-graph-view';
import { NarrativeHeader } from '@/components/narrative/narrative-header';
import { NarrativeListView } from '@/components/narrative/narrative-list-view';
import { NarrativeMapView } from '@/components/narrative/narrative-map-view';
import { StructureSelectionGate } from '@/components/narrative/structure-selection-gate';
import { Page } from '@/components/page';
import { QuickEventDialog } from '@/components/timeline/quick-event-dialog';
import { useFirebase } from '@/firebase';
import { useNarrativeItems } from '@/hooks/use-entities';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { filterEventsByFilters, type TimelineFilters as ITimelineFilters } from '@/lib/timeline-utils';
import { useTranslation } from 'react-i18next';

type ViewMode = 'list' | 'map' | 'graph';

function NarrativeStructureContent() {
  const { t } = useTranslation();
  const { backend } = useFirebase();
  const {
    novel,
    getSummaries
  } = useNovelRepository();
  const selectedNovelId = novel?.id;

  // State
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [filters, setFilters] = useState<ITimelineFilters>({
    locationIds: [],
    includeSublocations: true,
    characterIds: [],
    organizationIds: [],
    narrativeStatus: [],
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);

  // Data fetching
  const { data: arcs } = useNarrativeItems<NarrativeSummary>('arcs');
  const { data: scenes, isLoading: loadingScenes } = useNarrativeItems<NarrativeSummary>('scenes');
  const { data: structureSections } = useNarrativeItems<StructureSection>('structureSections');

  // Derive entities from novel summaries
  const events = getSummaries<EventSummary>('events');
  const locations = getSummaries<EntitySummary>('locations');
  const characters = getSummaries<EntitySummary>('characters');
  const organizations = getSummaries<EntitySummary>('organizations');

  // Filter events by section and filters
  const filteredEvents = useMemo(() => {
    if (!events) return [];

    let result = events;

    // Filter by selected section
    if (selectedSectionId) {
      result = result.filter((event) => event.structureSectionId === selectedSectionId);
    }

    // Apply additional filters
    return filterEventsByFilters(result, filters, locations || []);
  }, [events, selectedSectionId, filters, locations]);

  // Sort filtered events by date
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      return dateA.localeCompare(dateB);
    });
  }, [filteredEvents]);

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      locationIds: [],
      includeSublocations: true,
      characterIds: [],
      organizationIds: [],
      narrativeStatus: [],
    });
  };

  const handleToggleFilterDrawer = () => {
    setIsFilterDrawerOpen(!isFilterDrawerOpen);
  };

  // Template definitions for creating sections
  const TEMPLATES: Record<string, { id: string; name: string; order: number; color: string }[]> = {
    'heros-journey': [
      { id: 'ordinary-world', name: 'Ordinary World', order: 0, color: '#64748b' },
      { id: 'call-to-adventure', name: 'Call to Adventure', order: 1, color: '#3b82f6' },
      { id: 'refusal', name: 'Refusal of the Call', order: 2, color: '#ef4444' },
      { id: 'mentor', name: 'Meeting the Mentor', order: 3, color: '#10b981' },
      { id: 'threshold', name: 'Crossing the Threshold', order: 4, color: '#f59e0b' },
      { id: 'tests', name: 'Tests, Allies, Enemies', order: 5, color: '#8b5cf6' },
      { id: 'innermost-cave', name: 'Approach to Inmost Cave', order: 6, color: '#06b6d4' },
      { id: 'ordeal', name: 'Ordeal', order: 7, color: '#dc2626' },
      { id: 'reward', name: 'Reward', order: 8, color: '#eab308' },
      { id: 'road-back', name: 'The Road Back', order: 9, color: '#84cc16' },
      { id: 'resurrection', name: 'Resurrection', order: 10, color: '#a855f7' },
      { id: 'return-elixir', name: 'Return with the Elixir', order: 11, color: '#14b8a6' },
    ],
    'kishotenketsu': [
      { id: 'ki', name: 'Ki (起) - Introduction', order: 0, color: '#10b981' },
      { id: 'sho', name: 'Shō (承) - Development', order: 1, color: '#3b82f6' },
      { id: 'ten', name: 'Ten (転) - Twist', order: 2, color: '#f59e0b' },
      { id: 'ketsu', name: 'Ketsu (結) - Conclusion', order: 3, color: '#8b5cf6' },
    ],
    'fichtean-curve': [
      { id: 'inciting-crisis', name: 'Inciting Crisis', order: 0, color: '#ef4444' },
      { id: 'rising-crisis-1', name: 'Rising Crisis 1', order: 1, color: '#f97316' },
      { id: 'rising-crisis-2', name: 'Rising Crisis 2', order: 2, color: '#eab308' },
      { id: 'rising-crisis-3', name: 'Rising Crisis 3', order: 3, color: '#84cc16' },
      { id: 'climax', name: 'Climax', order: 4, color: '#dc2626' },
      { id: 'falling-action', name: 'Falling Action', order: 5, color: '#8b5cf6' },
    ],
    'three-act': [
      { id: 'act-1-setup', name: 'Act I - Setup', order: 0, color: '#3b82f6' },
      { id: 'act-2-confrontation', name: 'Act II - Confrontation', order: 1, color: '#f59e0b' },
      { id: 'act-3-resolution', name: 'Act III - Resolution', order: 2, color: '#10b981' },
    ],
    'custom': [
      { id: 'section-1', name: 'Section 1', order: 0, color: '#64748b' },
    ],
  };

  const handleTemplateChange = async (templateId: string) => {
    if (!selectedNovelId || !novel.id) return;

    try {
      const templateSections = TEMPLATES[templateId] || [];
      await backend.narrative.applyStructureTemplate(
        novel.ownerId,
        novel,
        templateId,
        templateSections,
        structureSections || []
      );

      // Reset section selection
      setSelectedSectionId(null);
    } catch (error) {
      console.error('Failed to update template:', error);
    }
  };

  // Show structure selection gate if no template selected
  if (novel && !novel.structureTemplateId && selectedNovelId) {
    return (
      <StructureSelectionGate />
    );
  }

  return (
    <AuthGuard>
      <Page
        title={t('narrative.header.title')}
        description={t('narrative.selection.description')}
        layoutMode="workspace"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', bgcolor: 'background.default' }}>
          {/* Header */}
          <NarrativeHeader
            filters={filters}
            onClearFilters={handleClearFilters}
            onQuickCreate={() => setIsQuickCreateOpen(true)}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            structureSections={structureSections || []}
            selectedStructureTemplateId={novel?.structureTemplateId}
            selectedSectionId={selectedSectionId}
            onSectionChange={setSelectedSectionId}
            onTemplateChange={handleTemplateChange}
            onToggleFilters={handleToggleFilterDrawer}
            activeFilterCount={
              filters.locationIds.length +
              filters.characterIds.length +
              filters.organizationIds.length +
              filters.narrativeStatus.length
            }
          />

          {/* Main Content Area */}
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Main View Area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              {viewMode === 'list' && (
                <NarrativeListView
                  events={sortedEvents}
                  scenes={scenes || []}
                  locations={locations || []}
                  characters={characters || []}
                  organizations={organizations || []}
                />
              )}
              {viewMode === 'map' && (
                <NarrativeMapView
                  events={sortedEvents}
                  locations={locations || []}
                  scenes={scenes || []}
                />
              )}
              {viewMode === 'graph' && (
                <NarrativeGraphView
                  events={sortedEvents}
                  arcs={arcs || []}
                  structureSections={structureSections || []}
                  locations={locations || []}
                  characters={characters || []}
                  organizations={organizations || []}
                  externalSelectedSectionId={selectedSectionId}
                  onExternalSectionChange={setSelectedSectionId}
                />
              )}
            </Box>

            {/* Right Column: Collapsible Filters */}
            <NarrativeFilters
              open={isFilterDrawerOpen}
              onClose={() => setIsFilterDrawerOpen(!isFilterDrawerOpen)}
              filters={filters}
              onFiltersChange={setFilters}
              locations={locations || []}
              characters={characters || []}
              organizations={organizations || []}
            />
          </Box>

          {/* Quick Create Dialog */}
          <QuickEventDialog
            open={isQuickCreateOpen}
            onClose={() => setIsQuickCreateOpen(false)}
            initialStructureSectionId={selectedSectionId}
          />
        </Box>
      </Page>
    </AuthGuard>
  );
}

export const Route = createFileRoute('/narrative-structure')({
  component: NarrativeStructurePage,
});

export default function NarrativeStructurePage() {
  return (
    <FeatureGuard featureKey="should_display_outline_page">
      <NarrativeStructureContent />
    </FeatureGuard>
  );
}
